import { useMutation } from '@apollo/client/react';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger
} from '@radix-ui/react-accordion';
import { type ColumnDef } from '@tanstack/react-table';
import {
  ArrowRightIcon,
  BanIcon,
  ChevronDownIcon,
  FileTextIcon,
  FolderOpenIcon,
  MoveDiagonalIcon,
  ZapIcon
} from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';

import {
  GENERATE_ICB_REPORT,
  GENERATE_ICM_REPORT,
  GENERATE_RSM_REPORT,
  UPDATE_OPPORTUNITY
} from './queries';
import type {
  DealSelects,
  GetDealOpportunitiesData,
  OpportunityNode
} from './types';
import { formatCurrencyValue } from './utils';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/Dialog';
import { PdfViewerDialog } from '@/components/reports/PdfViewerDialog';
import { TruncatedTextTooltip } from '@/components/TruncatedTextTooltip';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from '@/components/ui/empty';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { getInitials } from '@/utils/getInitials';

interface UpdateOpportunityResponse {
  updateOpportunity: { ok: boolean };
}

interface UpdateOpportunityVariables {
  id: string;
  killedDealNotes?: string;
  killReason?: string;
  status: string;
}

interface GenerateReportResponse {
  generateIcbReport?: { data: null | string; ok: boolean | null };
  generateIcmReport?: { data: null | string; ok: boolean | null };
  generateRsmReport?: { data: null | string; ok: boolean | null };
}

function getReportKind(
  phase: null | string | undefined
): 'icb' | 'icm' | 'rsm' | null {
  const normalized = (phase ?? '').trim().toLowerCase();
  if (normalized === 'icb') return 'icb';
  if (normalized === 'icm' || normalized === 'ficm') return 'icm';
  if (
    normalized === 'rsm' ||
    normalized === 'refined screening' ||
    normalized === 'screening'
  )
    return 'rsm';
  return null;
}

interface TruncatedBadgeProps {
  children: React.ReactNode;
  tooltipContent: string;
  variant?: 'pastel-slate';
}

const TruncatedBadge = ({
  children,
  tooltipContent,
  variant
}: TruncatedBadgeProps) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);

  const handleMouseEnter = () => {
    if (textRef.current) {
      setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  };

  return (
    <Tooltip open={isTruncated ? undefined : false}>
      <TooltipTrigger
        asChild
        onMouseEnter={handleMouseEnter}
      >
        <Badge
          className="max-w-full justify-start overflow-hidden"
          variant={variant}
        >
          <span
            className="block max-w-full min-w-0 truncate"
            ref={textRef}
          >
            {children}
          </span>
        </Badge>
      </TooltipTrigger>
      <TooltipContent className="max-w-md">{tooltipContent}</TooltipContent>
    </Tooltip>
  );
};

interface ActiveTabProps {
  data1?: GetDealOpportunitiesData;
  dealSelects: DealSelects;
  loading1: boolean;
  onRefresh: () => Promise<unknown>;
}

const DD_PROCESS_STAGES = [
  'Screening',
  'Refined Screening',
  'IOI',
  'ICB',
  'Term Sheet',
  'ICM',
  'FICM',
  'Legal, Tax, ESG',
  'Closing',
  'Post-Close'
] as const;

export const ActiveTab = ({
  data1,
  dealSelects,
  loading1,
  onRefresh
}: ActiveTabProps) => {
  const navigate = useNavigate();
  const [killDialogDeal, setKillDialogDeal] = useState<null | OpportunityNode>(
    null
  );
  const [killReason, setKillReason] = useState('');
  const [killNotes, setKillNotes] = useState('');
  const [isReportViewerOpen, setIsReportViewerOpen] = useState(false);
  const [reportViewerData, setReportViewerData] = useState<null | string>(null);
  const [reportViewerFilename, setReportViewerFilename] = useState('');

  const [updateOpportunity, { loading: isUpdating }] = useMutation<
    UpdateOpportunityResponse,
    UpdateOpportunityVariables
  >(UPDATE_OPPORTUNITY);

  const [generateRsmReport, { loading: isGeneratingRsm }] = useMutation<
    GenerateReportResponse,
    { id: string }
  >(GENERATE_RSM_REPORT);
  const [generateIcbReport, { loading: isGeneratingIcb }] = useMutation<
    GenerateReportResponse,
    { id: string }
  >(GENERATE_ICB_REPORT);
  const [generateIcmReport, { loading: isGeneratingIcm }] = useMutation<
    GenerateReportResponse,
    { id: string }
  >(GENERATE_ICM_REPORT);

  const handleKillDeal = useCallback(async () => {
    if (!killDialogDeal || !killReason) return;
    try {
      await updateOpportunity({
        variables: {
          id: killDialogDeal.id,
          killedDealNotes: killNotes || undefined,
          killReason,
          status: 'killed'
        }
      });
      await onRefresh();
      toast.success('Deal has been killed.');
      setKillDialogDeal(null);
      setKillReason('');
      setKillNotes('');
    } catch (error) {
      console.error(error);
      toast.error('Failed to kill deal.');
    }
  }, [killDialogDeal, killReason, killNotes, updateOpportunity, onRefresh]);

  const handleOpenReport = useCallback(
    async (deal: OpportunityNode) => {
      const reportKind = getReportKind(
        deal.dueDiligenceProjectId?.nextMilestoneName
      );
      if (!reportKind) return;

      try {
        const response =
          reportKind === 'rsm'
            ? await generateRsmReport({ variables: { id: deal.id } })
            : reportKind === 'icb'
              ? await generateIcbReport({ variables: { id: deal.id } })
              : await generateIcmReport({ variables: { id: deal.id } });

        const base64 =
          reportKind === 'rsm'
            ? response.data?.generateRsmReport?.data
            : reportKind === 'icb'
              ? response.data?.generateIcbReport?.data
              : response.data?.generateIcmReport?.data;

        if (!base64) {
          toast.error('Report is not available.');
          return;
        }

        const filename =
          reportKind === 'rsm'
            ? `${deal.name || 'deal'}-rsm-report.pdf`
            : reportKind === 'icb'
              ? `${deal.name || 'deal'}-icb-report.pdf`
              : `${deal.name || 'deal'}-icm-report.pdf`;

        setReportViewerData(base64);
        setReportViewerFilename(filename);
        setIsReportViewerOpen(true);
      } catch (error) {
        console.error(error);
        toast.error('Failed to open report.');
      }
    },
    [generateRsmReport, generateIcbReport, generateIcmReport]
  );

  const data1PerDDStage = useMemo(() => {
    const deals = data1?.opportunities.edges ?? [];
    return deals.reduce<Record<string, OpportunityNode[]>>((prev, curr) => {
      const nextMilestoneName =
        curr.node.dueDiligenceProjectId?.nextMilestoneName;
      if (!nextMilestoneName) return prev;
      if (nextMilestoneName in prev) {
        prev[nextMilestoneName].push(curr.node);
      } else {
        prev[nextMilestoneName] = [curr.node];
      }
      return prev;
    }, {});
  }, [data1]);

  const columns = useMemo<ColumnDef<OpportunityNode>[]>(
    () => [
      {
        cell: ({ row }) => (
          <div className="bg-background flex h-full w-full items-center justify-center">
            <Button
              asChild
              size="icon"
              variant="ghost"
            >
              <Link to={`/investments/direct/${row.original.id}/overview`}>
                <MoveDiagonalIcon />
              </Link>
            </Button>
          </div>
        ),
        enableSorting: false,
        header: () => <div className="bg-background h-full w-full" />,
        id: 'openDeal',
        meta: {
          cellClassName: 'p-0 bg-background',
          headerClassName: 'px-0 bg-background'
        },
        size: 52
      },
      {
        cell: ({ row }) => {
          const deal = row.original;
          const reportKind = getReportKind(
            deal.dueDiligenceProjectId?.nextMilestoneName
          );

          return (
            <div className="bg-background flex h-full w-full items-center justify-center">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    onClick={event => {
                      event.stopPropagation();
                    }}
                    size="icon"
                    variant="ghost"
                  >
                    <ZapIcon />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={event => {
                      event.stopPropagation();
                      navigate(
                        `/investments/direct/${deal.id}/due-diligence`
                      );
                    }}
                  >
                    <ArrowRightIcon className="size-4" />
                    Proceed to DD
                  </DropdownMenuItem>

                  {reportKind && (
                    <DropdownMenuItem
                      onClick={event => {
                        event.stopPropagation();
                        void handleOpenReport(deal);
                      }}
                    >
                      <FileTextIcon className="size-4" />
                      {reportKind === 'rsm'
                        ? 'Open RSM'
                        : reportKind === 'icb'
                          ? 'Open ICB'
                          : 'Open ICM'}
                    </DropdownMenuItem>
                  )}

                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={event => {
                      event.stopPropagation();
                      setKillDialogDeal(deal);
                      setKillReason('');
                      setKillNotes('');
                    }}
                    variant="destructive"
                  >
                    <BanIcon className="size-4" />
                    Kill
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
        enableSorting: false,
        header: () => <div className="bg-background h-full w-full" />,
        id: 'actions',
        meta: {
          cellClassName: 'p-0 bg-background',
          headerClassName: 'px-0 bg-background'
        },
        size: 52
      },
      {
        accessorKey: 'targetEntityName',
        cell: ({ row }) => (
          <div className="font-medium">
            <TruncatedTextTooltip>
              {row.original.targetEntityName || '-'}
            </TruncatedTextTooltip>
          </div>
        ),
        enableSorting: false,
        header: () => (
          <div className="w-[257px] text-xs font-semibold">Company Name</div>
        ),
        meta: {
          cellClassName: 'w-[257px]',
          headerClassName: 'w-[257px]'
        }
      },
      {
        accessorKey: 'projectName',
        cell: ({ row }) => (
          <TruncatedTextTooltip>
            {row.original.projectName || '-'}
          </TruncatedTextTooltip>
        ),
        header: () => (
          <div className="w-[173px] text-xs font-semibold">Project Name</div>
        ),
        meta: {
          cellClassName: 'w-[173px]',
          headerClassName: 'w-[173px]'
        }
      },
      {
        accessorKey: 'dealSource',
        cell: ({ row }) =>
          row.original.dealSource ? (
            <div className="flex items-center gap-x-2 overflow-hidden">
              <Avatar className="shrink-0">
                <AvatarFallback>
                  {getInitials(row.original.dealSource.name)}
                </AvatarFallback>
              </Avatar>
              <TruncatedTextTooltip>
                {row.original.dealSource.name}
              </TruncatedTextTooltip>
            </div>
          ) : (
            '-'
          ),
        enableSorting: false,
        header: () => (
          <div className="w-[173px] text-xs font-semibold">
            Deal Source Firm
          </div>
        ),
        meta: {
          cellClassName: 'w-[173px]',
          headerClassName: 'w-[173px]'
        }
      },
      {
        accessorKey: 'mdTeamId',
        cell: ({ row }) =>
          row.original.mdTeamId ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="*:data-[slot=avatar]:ring-background flex -space-x-2 *:data-[slot=avatar]:ring-2">
                  <Avatar key={row.original.mdTeamId.id}>
                    <AvatarImage
                      alt="@shadcn"
                      src={row.original.mdTeamId.name}
                    />
                    <AvatarFallback>
                      {row.original.mdTeamId.name.split(' ')[0].charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-md">
                {row.original.mdTeamId.name}
              </TooltipContent>
            </Tooltip>
          ) : (
            '-'
          ),
        enableSorting: false,
        header: () => (
          <div className="w-[86px] text-xs font-semibold">Deal Lead</div>
        ),
        meta: {
          cellClassName: 'w-[86px]',
          headerClassName: 'w-[86px]'
        }
      },
      {
        accessorKey: 'readableSmcIndustry',
        cell: ({ row }) =>
          row.original.readableSmcIndustry ? (
            <TruncatedBadge
              tooltipContent={row.original.readableSmcIndustry}
              variant="pastel-slate"
            >
              {row.original.readableSmcIndustry}
            </TruncatedBadge>
          ) : (
            '-'
          ),
        enableSorting: false,
        header: () => (
          <div className="w-[216px] text-xs font-semibold">Industry</div>
        ),
        meta: {
          cellClassName: 'w-[216px]',
          headerClassName: 'w-[216px]'
        }
      },
      {
        accessorKey: 'createDate',
        cell: ({ row }) => (
          <TruncatedTextTooltip>
            {row.original.createDate
              ? row.original.createDate.slice(0, 10)
              : '-'}
          </TruncatedTextTooltip>
        ),
        header: () => <div className="text-xs font-semibold">Created on</div>,
        meta: {
          cellClassName: 'w-[123px]',
          headerClassName: 'w-[123px]'
        }
      },
      {
        accessorKey: 'totalProposedDebtInvestment',
        cell: ({ row }) => (
          <div className="text-right">
            {row.original.totalProposedDebtInvestment != null ? (
              <TruncatedTextTooltip>
                {formatCurrencyValue(row.original.totalProposedDebtInvestment)}
              </TruncatedTextTooltip>
            ) : (
              '-'
            )}
          </div>
        ),
        header: () => (
          <div className="truncate text-right text-xs font-semibold whitespace-nowrap">
            Proposed Debt Investment
          </div>
        ),
        meta: {
          cellClassName: 'w-[173px]',
          headerClassName: 'w-[173px]'
        }
      },
      {
        accessorKey: 'totalProposedEquityInvestment',
        cell: ({ row }) => (
          <div className="text-right">
            {row.original.totalProposedEquityInvestment != null ? (
              <TruncatedTextTooltip>
                {formatCurrencyValue(
                  row.original.totalProposedEquityInvestment
                )}
              </TruncatedTextTooltip>
            ) : (
              '-'
            )}
          </div>
        ),
        header: () => (
          <div className="truncate text-right text-xs font-semibold whitespace-nowrap">
            Proposed Equity Investment
          </div>
        ),
        meta: {
          cellClassName: 'w-[173px]',
          headerClassName: 'w-[173px]'
        }
      },
      {
        accessorKey: 'ltmEbitda',
        cell: ({ row }) => (
          <div className="text-right">
            {row.original.ltmEbitda != null ? (
              <TruncatedTextTooltip>
                {formatCurrencyValue(row.original.ltmEbitda)}
              </TruncatedTextTooltip>
            ) : (
              '-'
            )}
          </div>
        ),
        header: () => (
          <div className="text-right text-xs font-semibold">EBITDA</div>
        ),
        meta: {
          cellClassName: 'w-[114px]',
          headerClassName: 'w-[114px]'
        }
      },
      {
        accessorKey: 'referencePoint',
        cell: ({ row }) => (
          <TruncatedTextTooltip>
            {row.original.referencePoint || '-'}
          </TruncatedTextTooltip>
        ),
        header: () => (
          <div className="w-[120px] text-xs font-semibold">Reference Point</div>
        ),
        meta: {
          cellClassName: 'w-[120px]',
          headerClassName: 'w-[120px]'
        }
      },
    ],
    [navigate, handleOpenReport]
  );

  if (loading1 && !data1?.opportunities) {
    return (
      <div className="flex min-h-[60vh] w-full items-center justify-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  const totalDeals = data1?.opportunities.edges.length ?? 0;

  if (totalDeals === 0) {
    return (
      <Empty className="pt-52!">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <FolderOpenIcon />
          </EmptyMedia>
          <EmptyTitle>No active deals</EmptyTitle>
          <EmptyDescription>
            There are no deals in the active pipeline yet.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <>
      <div className="h-full overflow-auto">
        <Accordion
          defaultValue={[]}
          type="multiple"
        >
          {DD_PROCESS_STAGES.map(dealStage => {
            const stageData = data1PerDDStage[dealStage] ?? [];
            const hasData = stageData.length > 0;
            const isCountOverflow = stageData.length > 999;
            const countLabel = isCountOverflow
              ? '999+'
              : String(stageData.length);
            return (
              <AccordionItem
                className="mb-2.5 rounded-md border p-5"
                disabled={!hasData}
                key={dealStage}
                value={dealStage}
              >
                <AccordionHeader>
                  <AccordionTrigger
                    className="group flex h-6.25 w-full items-center gap-x-5"
                    disabled={!hasData}
                  >
                    <ChevronDownIcon
                      aria-hidden
                      className={cn(
                        'transition-transform group-data-[state=open]:-rotate-z-180',
                        !hasData && 'text-muted-foreground'
                      )}
                      size={16}
                    />
                    {isCountOverflow ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge className="bg-dark-blue-primary-smc-100 text-dark-blue-primary-smc-500 w-[4ch] shrink-0 cursor-help justify-center px-0 tabular-nums">
                            {countLabel}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>{stageData.length}</TooltipContent>
                      </Tooltip>
                    ) : (
                      <Badge className="bg-dark-blue-primary-smc-100 text-dark-blue-primary-smc-500 w-[4ch] shrink-0 justify-center px-0 tabular-nums">
                        {countLabel}
                      </Badge>
                    )}
                    <div className="text-lg leading-normal font-semibold">
                      {dealStage}
                    </div>
                  </AccordionTrigger>
                </AccordionHeader>
                <AccordionContent className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down mt-5 overflow-hidden">
                  <DataTable
                    className="table-fixed"
                    columns={columns}
                    data={stageData}
                    enableColumnResizing
                    getRowId={row => row.id}
                    initialColumnPinning={{
                      left: ['openDeal', 'actions', 'targetEntityName']
                    }}
                    persistColumnSizingKey="direct-underwriting-active-table"
                  />
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>

      <Dialog
        onOpenChange={open => {
          if (!open) {
            setKillDialogDeal(null);
            setKillReason('');
            setKillNotes('');
          }
        }}
        open={Boolean(killDialogDeal)}
      >
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Kill Deal</DialogTitle>
          </DialogHeader>

          <DialogBody>
            <div className="space-y-4">
              <Select
                onValueChange={setKillReason}
                value={killReason}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {dealSelects.killReasonSelections.map(reason => (
                    <SelectItem
                      key={reason.id}
                      value={reason.value}
                    >
                      {reason.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Textarea
                onChange={event => {
                  setKillNotes(event.target.value);
                }}
                placeholder="Notes"
                value={killNotes}
              />
            </div>
          </DialogBody>

          <DialogFooter>
            <Button
              onClick={() => {
                setKillDialogDeal(null);
                setKillReason('');
                setKillNotes('');
              }}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              disabled={!killReason || isUpdating}
              onClick={() => {
                void handleKillDeal();
              }}
              variant="destructive"
            >
              {isUpdating ? 'Killing...' : 'Confirm Kill'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {(isGeneratingRsm || isGeneratingIcb || isGeneratingIcm) && (
        <div className="bg-background pointer-events-none fixed right-5 bottom-5 z-50 rounded-md border px-3 py-2 text-sm shadow-sm">
          Generating report...
        </div>
      )}

      <PdfViewerDialog
        description="View report content"
        filename={reportViewerFilename}
        onOpenChange={open => {
          setIsReportViewerOpen(open);
          if (!open) {
            setReportViewerData(null);
            setReportViewerFilename('');
          }
        }}
        open={isReportViewerOpen}
        pdfData={reportViewerData}
        title="Report Viewer"
      />
    </>
  );
};
